// Copyright (c) 2026, Cyber Cube and contributors
// For license information, please see license.txt
//
// The capture screen — `PHASE-9.md` §8.4 (desktop) and §8.5 (phone).
//
// **One module, one screen, two layouts.** §8.4's own heading says *"two-pane, one
// responsive screen"* and §8.5 says *"same page as 8.4, restacked"*, so the desk
// and the phone render the **same DOM** and the queue moves from a left rail to a
// swipe-up sheet by CSS. Two implementations of a capture screen is CONVENTIONS
// #24's drift with nothing comparing them, and this is the screen where a drift
// becomes a wrong verdict on a fire extinguisher.
//
// Imported by **both** bundle entries:
//   `qhse_facility.bundle.js`         → the desk, `/app/facility/round/<name>`
//   `qhse_facility_portal.bundle.js`  → the portal, `/inspect/<name>`
//
// ## The contract table under §8.5, line by line
//
// | Element | Where it is kept |
// |---|---|
// | sticky asset header | `.qhse-fac-asset` is `position: sticky` in the stylesheet — an inspector must never enter a reading against the wrong asset, and CSS stickiness is layout the browser computes rather than geometry this file measures (ARCH §10.31) |
// | unit of measure | rendered beside the input from the question's `uom`, as guidance text. Never written to the answer — `RESPONSE_FIELDS` has no `uom` |
// | live rule feedback | `qhse.facility.rules.evaluate` on every input event, and repeated server-side on save. The stored verdict is always `save_responses`' answer |
// | photo prompt | `rules.photoWanted` — `Always`, or `On Non-Compliance` once the live verdict is `Non-Compliant` |
// | fixed action bar | `_gate()` disables Save **with the reason in its `title`**, never a validation error on submit |
// | mandatory text acknowledgement (M9.19) | `_acknowledgeControl` renders **one** button, and only where the question is `mandatory` **and** Short/Long Text. It is what lets such a question stop holding the entry at `Pending` — before it, a round carrying one could never be closed at all |
// | `⬤ Scan` | `qhse.facility.scan.open`, which resolves through M9.8's `resolve_tag` and says by name when the asset is not in this round |
// | swipe-up queue | the same `<aside>` as the desktop rail, moved by a media query. `.is-open` is a class, not a computed height |
//
// ## Three rules this screen exists to obey
//
// **1. `.text()`, never markup.** Question text, `help_text`, `uom`,
// `section_label`, `select_options`, free-text answers, asset tags and verdict
// reasons are all author- or inspector-supplied. Everything is built through
// `qhse.facility.render`, whose `el()` sets `textContent` and which has no markup
// path at all (ARCH §10.24). There is no `innerHTML` in this file.
//
// **2. Nothing is read back out of a control** (CLAUDE.md §15.6/§15.7). Every
// answer lives in `state.answers`; a control is written *to* and its change handler
// writes *into* state. The M7.1 answer modal taught this the hard way: a hidden
// Frappe Select did not round-trip `set_value`, so clicking *Non-Compliant* left
// the risk block closed **and** left Save enabled with a mandatory field empty.
// `revalidate()` is public for the same reason — a programmatic write fires no DOM
// event, so the gate is re-run explicitly.
//
// **3. No Select is a literal here** (ARCH §10.13). The entry statuses, the verdict
// ladder and the Action category/priority arrive in `round_queue`'s
// `vocabularies`, read from `frappe.get_meta` **on the server** — because
// `frappe.model.with_doctype` needs `frappe.call`, which does not exist on the
// portal. The answer options are the question's own `select_options`, which is data
// rather than schema.

frappe.provide("qhse.facility");

const R = () => qhse.facility.render;
const NET = () => qhse.facility.net;
const RULES = () => qhse.facility.rules;

// The dotted method names are written **at the call site**, never hoisted into
// constants. `migration/verify_js.py`'s `CALL_METHOD` regex matches
// `.call("qhse.…")` literally and resolves each name against the Python source,
// so a constant would hide every endpoint this screen depends on from the one
// check that proves they exist — a check that quietly reads nothing is a check
// that reports green on the day it matters (CLAUDE.md §15.17).

// The queue filters of §8.5's sheet. Declared as data with their predicate, so
// "which rows does [Failed] show" has one definition and the pill labels cannot
// drift from it.
const FILTERS = [
	["all", "All", () => true],
	["pending", "Pending", (row) => row.status === "Pending" || row.status === "In Progress"],
	["failed", "Failed", (row) => row.verdict === "Fail" || row.responses_non_compliant > 0],
];

// How far a finger must travel on the handle before the sheet moves. A tap is a
// toggle; a drag of more than this in the right direction opens or closes.
//
// This is the whole of §10.31's exposure on this screen and it is **zero**: the
// gesture reads `Touch.clientY`, which is a pointer coordinate the event carries,
// not a measurement of an element. Nothing here calls `clientWidth`,
// `getBoundingClientRect`, `offsetHeight` or `scrollHeight`, so there is no
// hidden-element-reads-zero failure to guard against — the same "design it out"
// move M9.9b made three times, applied to the one surface that looked like it
// needed geometry.
const SWIPE_THRESHOLD = 40;

// The grouping key for a question whose `section_label` is blank — a value no
// author can type, so an unlabelled question can never collide with a real
// section.
//
// M9.16: this was written as a **raw NUL byte in the source**. One unprintable
// character made `file(1)` report the most safety-critical module in the phase
// — the one that decides verdicts — as `data`, and `grep -r` skip it silently
// without `-a`: an auditor searching this repo for `innerHTML`, `allow_guest` or
// any other banned pattern got **no** match from this file and no warning that
// it had been skipped. The escape is the same byte at runtime and plain ASCII in
// the file (`docs/FACILITY-DECISION-RECONCILIATION.md` `LS11`).
const NO_SECTION = "\u0000";

//: An action in one of these is finished, so its due date cannot be "overdue".
//: Stated here rather than inferred from a tone, because "not red" and "closed"
//: are different questions and only one of them is about the date.
const CLOSED_ACTION_STATUSES = ["Completed", "Cancelled"];

//: How many zone / type groups the scope summary lists before it counts the
//: rest. The box summarises the scope; the queue beside it is the manifest.
const SCOPE_GROUPS_SHOWN = 5;

//: A CSS-safe suffix for a state or timeliness name, so one stylesheet rule per
//: value can colour the chips without the markup carrying a colour decision.
//: Lower-cased and hyphenated: "Under Review" -> `is-under-review`.
const _slug = (value) =>
	String(value || "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");

const state = {
	mounted: false,
	container: null,
	roundName: null,
	round: null,
	entries: [],
	index: -1,
	entry: null,
	questions: [],
	answers: {},
	live: {},
	dirty: {},
	today: null,
	user: null,
	vocabularies: {},
	filter: "all",
	//: M9.40 — `"capture"` or `"actions"`. The Capture tab is what this screen
	//: has always been; the Actions tab swaps the main pane and hides the asset
	//: queue and the per-asset action bar, because neither means anything there.
	tab: "capture",
	actions: null,
	actionsLoaded: false,
	//: M9.40 (option 1A) — what the round's stored scope matches RIGHT NOW, for
	//: a round whose scope is not resolved yet. A preview, never a resolution:
	//: nothing is written, and `Start Inspection` remains the one-way door.
	preview: null,
	sheetOpen: false,
	busy: false,
	nodes: {},
	onExit: null,
	exitLabel: null,
	//: M9.32 — the last `round_workflow` payload. Held so a repaint after a
	//: transition has something to draw before the next round trip returns, and
	//: so a test can read what the screen was told rather than what it shows.
	lifecycle: null,
	//: What `render` was called with, so a transition can re-enter it. Starting a
	//: round resolves its scope, which changes the questionnaire and the queue —
	//: repainting only the button bar would leave both stale.
	options: null,
};

qhse.facility.capture = {
	/** Render the capture screen for one round.
	 *
	 * `options.entry` opens a specific entry; `options.onExit` is what the back
	 * control does (the desk routes, the portal navigates). Both shells call this
	 * one function with the same arguments.
	 *
	 * `options.exitLabel` names where back goes. M9.16: the label was the string
	 * *"Back to my rounds"* for both callers, which is the portal's wording on a
	 * desk screen that has gone back to the inspection list since M9.13 — the
	 * caller owns the destination, so the caller owns the word for it. The
	 * default stays deliberately neutral rather than naming either shell.
	 */
	render(container, roundName, options) {
		const opts = options || {};
		state.container = container;
		state.options = opts;
		state.roundName = roundName || null;
		state.onExit = opts.onExit || null;
		state.exitLabel = opts.exitLabel || null;
		state.mounted = true;

		if (!state.roundName) {
			container.appendChild(
				R().empty(
					__("No inspection round was named."),
					__("Open a round from your list to start capturing answers.")
				)
			);
			return Promise.resolve();
		}

		R().clear(container);
		container.appendChild(R().empty(__("Loading the round…"), ""));

		return NET()
			.call("qhse.api.facility_pages.round_queue", { inspection: state.roundName })
			.then((payload) => {
				state.round = (payload && payload.round) || {};
				state.entries = (payload && payload.entries) || [];
				state.today = (payload && payload.today) || null;
				state.user = (payload && payload.user) || null;
				state.vocabularies = (payload && payload.vocabularies) || {};
				state.outOfScope = (payload && payload.out_of_scope_status) || "Out of Scope";
				state.canWrite = !!(payload && payload.can_write);
				state.tab = "capture";
				state.actions = null;
				state.actionsLoaded = false;
				state.preview = null;
				this._paint();
				// A round with no entries has a scope but no list yet. Ask what it
				// matches so the queue can show it (M9.40, option 1A).
				if (!state.entries.length) this._loadPreview();
				const wanted = opts.entry
					? state.entries.findIndex((row) => row.name === opts.entry)
					: -1;
				return this._openIndex(wanted >= 0 ? wanted : this._firstOpenIndex());
			})
			.catch((error) => {
				R().clear(container);
				container.appendChild(
					R().empty(
						__("This round could not be opened."),
						error && error.message ? String(error.message) : ""
					)
				);
			});
	},

	/** Re-run the live feedback and the Save gate.
	 *
	 * Public because a **programmatic** write fires no `input` or `change` event
	 * (CLAUDE.md §15.7: Smart Analysis adopting a suggestion left Save disabled
	 * with a filled field on screen and no way out but typing a character). The
	 * scanner calls it after jumping to an asset; anything that writes state
	 * without a user keystroke must call it too.
	 */
	revalidate() {
		if (!state.mounted || !state.entry) return;
		this._evaluateAll();
		this._paintFeedback();
		this._gate();
	},

	/** The current entry, for the scanner and for tests. Read-only by convention. */
	current() {
		return state.entry ? { entry: state.entry, round: state.round } : null;
	},

	/** Jump to the queue row for `asset`, or say why not (§8.5's scan contract).
	 *
	 * Returns true when it moved. A code that resolves to an asset **not in this
	 * round** is the case `PHASE-9.md` §8.5 calls out by name: it must say so
	 * rather than silently doing nothing, which is the failure M9.8 built
	 * `parse_code` to avoid.
	 */
	jumpToAsset(asset, tag) {
		const index = state.entries.findIndex((row) => row.asset === asset);
		if (index < 0) {
			frappe.show_alert({
				message: __("{0} is not an asset in this round.", [tag || asset]),
				indicator: "orange",
			});
			return false;
		}
		this._openIndex(index);
		return true;
	},

	// ----------------------------------------------------------------- chrome

	_paint() {
		const container = R().clear(state.container);
		const wrap = R().el("div", { class: "qhse-fac-capture" });

		wrap.appendChild(this._roundBar());

		wrap.appendChild(this._tabs());

		const body = R().el("div", { class: "qhse-fac-capture-body" });
		state.nodes.queue = this._queue();
		state.nodes.main = R().el("main", { class: "qhse-fac-capture-main" });
		R().append(body, state.nodes.queue, state.nodes.main);
		wrap.appendChild(body);
		state.nodes.body = body;

		state.nodes.bar = this._actionBar();
		wrap.appendChild(state.nodes.bar);
		this._applyTab();
		container.appendChild(wrap);
		state.nodes.wrap = wrap;
	},

	_roundBar() {
		const bar = R().el("header", { class: "qhse-fac-capture-bar" });
		const row = R().el("div", { class: "qhse-fac-capture-bar-row" });

		const back = R().iconButton("back", state.exitLabel || __("Back"), () => {
			if (state.onExit) state.onExit();
		});
		back.classList.add("qhse-fac-capture-back");

		const title = R().el("div", { class: "qhse-fac-capture-title" });
		R().append(
			title,
			R().el("strong", {}, state.round.name || state.roundName),
			R().el("span", { class: "qhse-fac-capture-subtitle" }, state.round.title || ""),
			// M9.27, the owner's request: *"ensure assigned to is also visible to
			// user that which inspector/user would do the inspection."* This is
			// the screen where it matters most — an inspector standing in front
			// of an asset should be able to see the round is theirs. Unassigned
			// is stated rather than left blank: an empty line reads as a missing
			// value, and "nobody yet" is a fact worth showing.
			R().el(
				"span",
				{ class: "qhse-fac-capture-assignee" },
				state.round.assigned_to
					? __("Inspector: {0}", [
							state.round.assigned_to_name || state.round.assigned_to,
					  ])
					: __("Inspector: unassigned")
			)
		);

		state.nodes.counter = R().el("span", { class: "qhse-fac-capture-counter" }, "");
		// M9.25. This was `R().iconButton("qr", …)` — a 15px glyph among the
		// header's other glyphs, on the screen the owner calls "mainly used on
		// the phone", for the action that screen exists to make fast. It is now a
		// labelled button with its icon, filled, and 44px tall on a handset.
		const scan = R().el("button", {
			type: "button",
			class: "qhse-fac-button is-primary qhse-fac-capture-scan",
			title: __("Scan an asset"),
			"aria-label": __("Scan an asset"),
		});
		const scanGlyph = R().icon("qr", 18);
		if (scanGlyph) scan.appendChild(scanGlyph);
		scan.appendChild(R().el("span", { class: "qhse-fac-capture-scan-label" }, __("Scan")));
		scan.addEventListener("click", () => this._scan());

		// M9.30. Print the whole round — cover, summary, then a page per asset
		// (the `Facility Inspection Record` format M9.29 ships). `printview` is
		// frappe's own route and takes the format by name, so this is a link to a
		// real URL rather than a client-side render: the browser's own print
		// dialog then offers Save as PDF, on every platform, with no dependency.
		const print = R().el("button", {
			type: "button",
			class: "qhse-fac-button qhse-fac-capture-print",
			title: __("Print this round"),
			"aria-label": __("Print this round"),
		});
		const printGlyph = R().icon("report", 16);
		if (printGlyph) print.appendChild(printGlyph);
		print.appendChild(R().el("span", { class: "qhse-fac-capture-print-label" }, __("Print")));
		print.addEventListener("click", () => this._print());

		R().append(row, back, title, state.nodes.counter, print, scan);
		bar.appendChild(row);

		// M9.32 — the round's own workflow, on the round's own page.
		//
		// The round is a custom desk Page, not a form, so frappe's action strip
		// never renders here: the screen an inspector actually holds had no way
		// to accept a round, start it, submit it or approve it, and the owner
		// asked for those buttons by name. The bar is filled asynchronously
		// because `get_transitions` is a server decision — which transitions
		// exist depends on the caller's roles AND on each transition's
		// condition, and re-deriving either in JavaScript would be a second copy
		// of the state machine (§15.26).
		state.nodes.lifecycle = R().el("div", { class: "qhse-fac-lifecycle" });
		bar.appendChild(state.nodes.lifecycle);
		this._refreshLifecycle();

		state.nodes.progress = R().el("div", { class: "qhse-fac-progress" });
		state.nodes.progressFill = R().el("div", { class: "qhse-fac-progress-fill" });
		state.nodes.progress.appendChild(state.nodes.progressFill);
		bar.appendChild(state.nodes.progress);

		if (state.round.needs_resync) {
			bar.appendChild(
				R().banner(
					"warn",
					__("Scope edited — re-sync needed"),
					__("This round's scope has changed since it was resolved. Ask a planner to re-sync it before relying on the asset list.")
				)
			);
		}
		return bar;
	},

	/** §8.4's left rail and §8.5's swipe-up sheet — the same element. */
	_queue() {
		const aside = R().el("aside", { class: "qhse-fac-queue" });

		// The handle is a real button, so the sheet is reachable without a touch
		// screen and announces itself to a screen reader. The swipe is an
		// enhancement on top of it, never the only way in.
		const handle = R().el("button", {
			type: "button",
			class: "qhse-fac-queue-handle",
			"aria-expanded": "false",
			title: __("Show the asset queue"),
		});
		handle.appendChild(R().el("span", { class: "qhse-fac-queue-grip" }, ""));
		const summary = R().el("span", { class: "qhse-fac-queue-summary" }, "");
		handle.appendChild(summary);
		// M9.25. A 4px grip and a count read as decoration; a caret that flips
		// says "this opens". `state.nodes.summary` is taken from the variable
		// rather than `handle.lastChild`, which this line would otherwise have
		// silently repointed at the caret.
		handle.appendChild(R().el("span", { class: "qhse-fac-queue-caret" }, "\u25B2"));
		handle.addEventListener("click", () => this._toggleSheet());
		this._bindSwipe(handle);
		state.nodes.handle = handle;
		state.nodes.summary = summary;
		aside.appendChild(handle);

		// M9.25 — the owner's report: "to close the dock expanded window there is
		// no cross button or clearly visible". The handle does toggle, but the
		// close path has to be findable without knowing that. Rendered always and
		// revealed by `.is-open` in CSS, so opening the sheet cannot fail to
		// produce its own way out.
		const close = R().el(
			"button",
			{
				type: "button",
				class: "qhse-fac-queue-close",
				title: __("Close the asset queue"),
				"aria-label": __("Close the asset queue"),
			},
			"\u2715"
		);
		close.addEventListener("click", (event) => {
			// The handle is the sheet's other control and sits behind this one.
			event.stopPropagation();
			this._setSheet(false);
		});
		state.nodes.close = close;
		aside.appendChild(close);

		const filters = R().el("div", { class: "qhse-fac-queue-filters" });
		filters.appendChild(
			R().pills(
				FILTERS.map(([key, label]) => [key, __(label)]),
				state.filter,
				(value) => {
					state.filter = value;
					this._paintQueue();
				}
			)
		);
		aside.appendChild(filters);

		state.nodes.rows = R().el("div", { class: "qhse-fac-queue-rows" });
		aside.appendChild(state.nodes.rows);
		return aside;
	},

	/** Touch-only sheet gesture — pointer coordinates, never element geometry.
	 *
	 * ARCH §10.31 is about code that measures its own container and reads zero
	 * when the container has no layout, silently, while every check written
	 * against it reports green. This gesture cannot hit that: it compares two
	 * `Touch.clientY` values the browser puts on the event. There is nothing to
	 * guard because there is nothing measured.
	 */
	_bindSwipe(handle) {
		let startY = null;
		handle.addEventListener(
			"touchstart",
			(event) => {
				startY = event.touches && event.touches.length ? event.touches[0].clientY : null;
			},
			{ passive: true }
		);
		handle.addEventListener(
			"touchend",
			(event) => {
				if (startY === null) return;
				const touch = event.changedTouches && event.changedTouches[0];
				if (!touch) return;
				const travel = startY - touch.clientY;
				startY = null;
				if (travel > SWIPE_THRESHOLD) this._setSheet(true);
				else if (travel < -SWIPE_THRESHOLD) this._setSheet(false);
			},
			{ passive: true }
		);
	},

	_toggleSheet() {
		this._setSheet(!state.sheetOpen);
	},

	_setSheet(open) {
		state.sheetOpen = !!open;
		// The consequence a user sees is the class, and the class is what the
		// stylesheet moves. Asserting "the handler ran" would prove nothing
		// (CLAUDE.md §15.6 rule 2).
		state.nodes.queue.classList.toggle("is-open", state.sheetOpen);
		state.nodes.handle.setAttribute("aria-expanded", state.sheetOpen ? "true" : "false");
		state.nodes.handle.setAttribute(
			"title",
			state.sheetOpen ? __("Hide the asset queue") : __("Show the asset queue")
		);
	},

	// -----------------------------------------------------------------------
	// M9.40 — the tab strip, and the round's own actions
	// -----------------------------------------------------------------------

	/** `Capture | Actions N`, under the lifecycle bar.
	 *
	 * Chosen from wireframes (option 2A) over a segmented control in the header
	 * row, which on a phone is already carrying back · title · counter · Scan ·
	 * Print and would have wrapped; and over a second slide-up sheet, which
	 * would have fought the asset queue for the same bottom edge and the same
	 * gesture. A two-item strip is the least this can be.
	 */
	_tabs() {
		const strip = R().el("div", { class: "qhse-fac-tabs", role: "tablist" });
		state.nodes.tabs = {};

		[
			["capture", __("Capture"), null],
			["actions", __("Actions"), () => this._actionCount()],
		].forEach(([key, label, count]) => {
			const tab = R().el("button", {
				type: "button",
				role: "tab",
				class: "qhse-fac-tab",
				"data-qhse-tab": key,
			});
			tab.appendChild(R().el("span", {}, label));
			if (count) {
				const badge = R().el("span", { class: "qhse-fac-tab-count" }, "");
				tab.appendChild(badge);
				state.nodes.tabCount = badge;
			}
			tab.addEventListener("click", () => this._openTab(key));
			state.nodes.tabs[key] = tab;
			strip.appendChild(tab);
		});
		return strip;
	},

	_actionCount() {
		return Array.isArray(state.actions) ? state.actions.length : null;
	},

	_openTab(key) {
		if (state.tab === key) return;
		state.tab = key;
		this._applyTab();
		if (key === "actions" && !state.actionsLoaded) this._loadActions();
	},

	/** Show the pane the current tab owns, and hide what does not belong to it.
	 *
	 * The queue and the fixed `[+ Action] [Save & next]` bar both act on the
	 * CURRENT ASSET. On the Actions tab there is no current asset, so both are
	 * hidden rather than left on screen doing nothing — a disabled control that
	 * can never enable is worse than an absent one.
	 *
	 * `hidden` rather than a class, so the elements are out of the accessibility
	 * tree as well as out of the layout.
	 */
	_applyTab() {
		Object.keys(state.nodes.tabs || {}).forEach((key) => {
			const tab = state.nodes.tabs[key];
			const on = key === state.tab;
			tab.classList.toggle("is-active", on);
			tab.setAttribute("aria-selected", on ? "true" : "false");
		});

		const onCapture = state.tab === "capture";
		if (state.nodes.queue) state.nodes.queue.hidden = !onCapture;
		if (state.nodes.bar) state.nodes.bar.hidden = !onCapture;
		if (state.nodes.body) {
			state.nodes.body.classList.toggle("is-actions", !onCapture);
		}
		this._paintTabCount();

		if (onCapture) {
			// Repaint the asset the inspector was on, exactly as it was.
			if (state.entry) this._paintEntry();
			else this._paintMainEmpty();
		} else {
			this._paintActions();
		}
	},

	_paintTabCount() {
		const badge = state.nodes.tabCount;
		if (!badge) return;
		const count = this._actionCount();
		badge.textContent = count === null ? "" : String(count);
		badge.hidden = count === null;
	},

	_loadActions() {
		state.actionsLoaded = true;
		NET()
			.call("qhse.api.facility_inspections.linked_actions", {
				inspection: state.roundName,
			})
			.then((rows) => {
				state.actions = Array.isArray(rows) ? rows : [];
				this._paintTabCount();
				if (state.tab === "actions") this._paintActions();
			})
			.catch((error) => {
				state.actionsLoaded = false;
				if (state.tab === "actions") {
					R().clear(state.nodes.main).appendChild(
						R().empty(
							__("The actions could not be loaded."),
							error && error.message ? String(error.message) : ""
						)
					);
				}
			});
	},

	/** Every action this round raised — per-asset and round-level, in one list.
	 *
	 * The owner asked for both. They are told apart by the Asset column: an
	 * action carrying an entry names its asset, one raised against the round
	 * reads "— whole round". A blank there would read as missing data rather
	 * than as a different kind of finding.
	 */
	_paintActions() {
		const main = R().clear(state.nodes.main);

		if (!state.actionsLoaded || state.actions === null) {
			main.appendChild(R().empty(__("Loading the actions…"), ""));
			return;
		}
		if (!state.actions.length) {
			main.appendChild(
				R().empty(
					__("No corrective actions on this round."),
					__("Actions raised while inspecting an asset appear here, " +
					   "alongside any raised against the round itself.")
				)
			);
			return;
		}

		const table = R().el("table", { class: "qhse-fac-actions-table" });
		const head = R().el("thead");
		const headRow = R().el("tr");
		[__("Subject"), __("Asset"), __("Assigned to"), __("Due"), __("Status")]
			.forEach((label) => headRow.appendChild(R().el("th", {}, label)));
		head.appendChild(headRow);
		table.appendChild(head);

		const tbody = R().el("tbody");
		state.actions.forEach((row) => tbody.appendChild(this._actionRow(row)));
		table.appendChild(tbody);
		main.appendChild(table);
	},

	_actionRow(row) {
		const overdue = this._isOverdue(row);
		const tr = R().el("tr", {
			class: `qhse-fac-actions-row${overdue ? " is-overdue" : ""}`,
		});

		const subject = R().el("td", { class: "qhse-fac-actions-subject" });
		subject.appendChild(R().el("span", {}, row.subject || row.name));
		// The question the action came from, when it came from one — it is what
		// makes a subject like "Replace the hose" traceable.
		if (row.facility_question_code) {
			subject.appendChild(
				R().el("span", { class: "qhse-fac-actions-code" }, row.facility_question_code)
			);
		}
		tr.appendChild(subject);

		const asset = R().el("td", { class: "qhse-fac-actions-asset" });
		if (row.asset_tag || row.asset_name) {
			asset.appendChild(R().el("strong", {}, row.asset_tag || row.asset));
			if (row.asset_name) {
				asset.appendChild(R().el("span", {}, row.asset_name));
			}
		} else {
			asset.appendChild(
				R().el("span", { class: "qhse-fac-actions-whole" }, __("— whole round"))
			);
		}
		tr.appendChild(asset);

		tr.appendChild(
			R().el("td", {}, row.assigned_to_full_name || row.assigned_to || __("unassigned"))
		);

		const due = R().el("td", { class: "qhse-fac-actions-due" });
		due.appendChild(R().el("span", {}, row.due_date || "—"));
		if (overdue) {
			due.appendChild(R().el("span", { class: "qhse-fac-chip is-bad" }, __("Overdue")));
		}
		tr.appendChild(due);

		tr.appendChild(
			R().el(
				"td",
				{},
				R().el(
					"span",
					{ class: `qhse-fac-chip${this._actionTone(row.status)}` },
					row.status || "—"
				)
			)
		);
		return tr;
	},

	//: An action is overdue when its due date has passed and it is still open.
	//: `state.today` is the SERVER's date, already on this screen — reading the
	//: browser's clock would make the flag depend on the tablet's timezone.
	_isOverdue(row) {
		if (!row.due_date || !state.today) return false;
		return row.due_date < state.today && !CLOSED_ACTION_STATUSES.includes(row.status);
	},

	_actionTone(status) {
		if (status === "Completed") return " is-good";
		if (status === "Cancelled") return "";
		if (status === "Pending Start") return " is-warn";
		return "";
	},

	_paintQueue() {
		const predicate = (FILTERS.find(([key]) => key === state.filter) || FILTERS[0])[2];
		const rows = R().clear(state.nodes.rows);
		const visible = state.entries.filter(predicate);

		// M9.40, option 1A — a round with no entries is not an empty round, it is
		// a round that has not started. Show what its scope matches now, so the
		// inspector can see what they will walk before they commit to walking it.
		if (!state.entries.length && state.preview && state.preview.assets) {
			rows.appendChild(
				R().el(
					"p",
					{ class: "qhse-fac-queue-preview-note" },
					__("Not locked in yet — this is what the scope matches today.")
				)
			);
			state.preview.assets.forEach((asset) => {
				// The queue's own inner classes, so a preview row lays out exactly
				// like a real one — `qhse-fac-queue-row` carries no layout of its
				// own, the three spans inside it do. A `<div>`, not a `<button>`:
				// there is nothing to open until the scope resolves, and a control
				// that does nothing when tapped reads as broken.
				const item = R().el("div", { class: "qhse-fac-queue-row is-preview" });
				// The row is a 3-column grid, `20px | minmax(0,1fr) | auto`, and the
				// third column is `auto` — so anything long in the note slot takes
				// the width and squeezes the tag to nothing. The real rows put a
				// short progress note there; a preview has no progress, so it puts
				// nothing, and the asset's own name is not shown here for the same
				// reason the real rows do not show it: the tag is the identifier an
				// inspector reads off the sticker.
				R().append(
					item,
					R().verdictGlyph(null),
					R().el("span", { class: "qhse-fac-queue-tag" }, asset.asset_tag || asset.name),
					R().el(
						"span",
						{ class: "qhse-fac-queue-zone", title: asset.zone_path || "" },
						R().trail(asset.zone_path, 2)
					),
					R().el("span", { class: "qhse-fac-queue-note" }, "")
				);
				const line = R().el("div", { class: "qhse-fac-queue-line" });
				line.appendChild(item);
				rows.appendChild(line);
			});
			// Set here, because this branch returns before the summary below it.
			state.nodes.summary.textContent = __("{0} assets · preview", [
				String(state.preview.to_inspect || state.preview.assets.length),
			]);
			this._paintProgress(0);
			return;
		}

		if (!visible.length) {
			rows.appendChild(
				R().empty(
					state.entries.length
						? __("No asset matches this filter.")
						: __("This round has no assets yet."),
					state.entries.length
						? ""
						: // Naming the button, and the right actor. The old copy said
						  // "a planner resolves its scope", which has not been true
						  // since M9.32: the inspector does it with Start Inspection,
						  // from this screen (M9.40).
						  __("Press Start Inspection to resolve the scope and begin.")
				)
			);
		}

		visible.forEach((row) => {
			const index = state.entries.indexOf(row);
			const item = R().el("button", {
				type: "button",
				class: `qhse-fac-queue-row${index === state.index ? " is-current" : ""}${
					row.status === state.outOfScope ? " is-out-of-scope" : ""
				}`,
			});
			R().append(
				item,
				R().verdictGlyph(this._queueVerdict(row)),
				R().el("span", { class: "qhse-fac-queue-tag" }, row.asset_tag || row.asset),
				R().el("span", { class: "qhse-fac-queue-zone" }, row.zone_path || ""),
				R().el("span", { class: "qhse-fac-queue-note" }, this._queueNote(row))
			);
			item.addEventListener("click", () => {
				this._setSheet(false);
				this._openIndex(index);
			});
			// M9.30. The row is a `<button>`, so the print control cannot nest
			// inside it — invalid HTML, and a click target inside a click target
			// is ambiguous to a screen reader and to a thumb. They are siblings
			// in a flex line instead.
			const line = R().el("div", { class: "qhse-fac-queue-line" });
			line.appendChild(item);
			line.appendChild(
				R().iconButton(
					"report",
					__("Print this asset's record"),
					() => this._printEntry(row.name),
					{ class: "qhse-fac-queue-print", size: 14 }
				)
			);
			rows.appendChild(line);
		});

		const done = state.entries.filter((row) => row.status === "Completed").length;
		state.nodes.summary.textContent = __("{0} assets · {1} done", [
			String(state.entries.length),
			String(done),
		]);
		this._paintProgress(done);
	},

	/** The glyph's meaning: the stored verdict, or "never" when nothing is stored.
	 *
	 * `render.verdictGlyph` maps `Pass → ✓`, `Pass with Observations`/`Fail → ⚠`
	 * and anything else → `○`, with the real value in the `title`. `Pending` is
	 * deliberately the `○` case: an entry nobody has answered is not an outcome.
	 */
	_queueVerdict(row) {
		if (!row.verdict || row.verdict === "Pending") return null;
		return row.verdict;
	},

	_queueNote(row) {
		if (row.status === state.outOfScope) return __("out of scope");
		if (row.responses_non_compliant > 0) {
			return __("{0} fails", [String(row.responses_non_compliant)]);
		}
		if (row.status === "Completed") return __("done");
		if (row.status === "Skipped") return __("skipped");
		if (row.responses_pending > 0 && row.total_responses > 0) {
			return `${row.total_responses - row.responses_pending}/${row.total_responses}`;
		}
		return "";
	},

	_paintProgress(done) {
		const total = state.entries.length;
		const completed = done === undefined
			? state.entries.filter((row) => row.status === "Completed").length
			: done;
		state.nodes.counter.textContent = `${completed}/${total}`;
		// A percentage written into a style property, never into markup. `total`
		// of 0 is the fresh-site case and must render an empty bar rather than
		// `NaN%` — which is exactly what `completed / 0 * 100` produces.
		const pct = total ? Math.round((completed / total) * 100) : 0;
		state.nodes.progressFill.style.width = `${pct}%`;
		state.nodes.progress.setAttribute("aria-valuenow", String(pct));
	},

	// ------------------------------------------------------------- one entry

	_firstOpenIndex() {
		const open = state.entries.findIndex(
			(row) => row.status === "Pending" || row.status === "In Progress"
		);
		return open >= 0 ? open : state.entries.length ? 0 : -1;
	},

	/** The main pane when there is no asset open.
	 *
	 * Two different rounds land here and they are not the same situation, so
	 * they do not get the same sentence:
	 *
	 *  * a round whose scope is **not resolved yet** — the normal state of every
	 *    Planned round — gets its scope, the live match count, and the name of
	 *    the button that locks the list in;
	 *  * a round that is resolved and genuinely has nothing gets the old line.
	 *
	 * Before M9.40 both got *"This round has no assets in scope yet"*, which is
	 * wrong for the first and is what made a correctly-planned round look broken.
	 * The old copy also named a "planner" resolving the scope, which has not been
	 * true since M9.32 — it is the inspector, with `Start Inspection`.
	 */
	_paintMainEmpty() {
		const main = R().clear(state.nodes.main);
		const preview = state.preview;

		if (!state.round.scope_resolved_on && preview && preview.to_inspect) {
			const box = R().el("div", { class: "qhse-fac-preview" });
			box.appendChild(
				R().el("h3", { class: "qhse-fac-preview-title" }, __("Not started yet"))
			);
			box.appendChild(
				R().el(
					"p",
					{ class: "qhse-fac-preview-lead" },
					__("These {0} assets are what this round's scope matches today.",
					   [String(preview.to_inspect)])
				)
			);
			box.appendChild(
				R().el(
					"p",
					{ class: "qhse-fac-preview-note" },
					__("The list is not locked in yet. Press Start Inspection to fix it " +
					   "and begin — until then it follows the register, so an asset " +
					   "added or retired before the round is walked is picked up by " +
					   "itself.")
				)
			);
			const scope = R().el("dl", { class: "qhse-fac-preview-scope" });
			// `facility_scope.group_counts` ships `{label, count}` per row — the
			// breakdown the planning rail already draws. Rendering the count with
			// the label is what makes this a scope rather than a list of names.
			// `group_counts` groups zones by the FULL breadcrumb, so every distinct
			// path is its own group and each is six hops long on this estate — 17
			// groups rendered a wall of text nobody reads. Trimmed to the last two
			// hops (`R().trail`, M9.35's helper, with the full path on hover) and
			// capped, because this box is a summary of the scope, not a manifest:
			// the manifest is the queue on the left.
			[
				[__("Zones"), preview.by_zone],
				[__("Asset types"), preview.by_type],
			].forEach(([label, groups]) => {
				const rows = (groups || []).filter((row) => row && row.label);
				if (!rows.length) return;
				const shown = rows.slice(0, SCOPE_GROUPS_SHOWN);
				const dd = R().el("dd", {});
				shown.forEach((row, index) => {
					if (index) dd.appendChild(R().el("span", {}, " · "));
					// `R().trail()` returns a NODE. Interpolating it into a template
					// literal renders `[object HTMLSpanElement]` — M9.33's lesson
					// arriving from the other side: there `el()` stringified a node
					// it was handed, here the caller stringified one it asked for.
					// A template literal is a stringification no linter flags.
					const group = R().el("span", { title: row.label });
					group.appendChild(R().trail(row.label, 2));
					group.appendChild(R().el("span", {}, ` (${row.count})`));
					dd.appendChild(group);
				});
				if (rows.length > shown.length) {
					dd.appendChild(
						R().el(
							"span",
							{ class: "qhse-fac-preview-more" },
							__(" · and {0} more", [String(rows.length - shown.length)])
						)
					);
				}
				scope.appendChild(R().el("dt", {}, label));
				scope.appendChild(dd);
			});
			if (preview.excluded) {
				scope.appendChild(R().el("dt", {}, __("Excluded")));
				scope.appendChild(
					R().el("dd", {}, __("{0} asset(s)", [String(preview.excluded)]))
				);
			}
			box.appendChild(scope);
			main.appendChild(box);
			return;
		}

		main.appendChild(
			R().empty(
				__("Nothing to capture."),
				state.round.scope_resolved_on
					? __("This round resolved its scope and matched no assets.")
					: __("This round's scope matches no assets yet.")
			)
		);
	},

	/** Ask what the stored scope matches, for a round with no entries.
	 *
	 * **A read, and only a read.** `preview_scope` is `@frappe.read_only()` and
	 * resolves the round's saved scope through the same `resolve_scope` that
	 * `resolve_and_open` uses — so the preview and the eventual resolution
	 * cannot disagree about what the scope means. Nothing is written: no entry,
	 * no `scope_resolved_on`, not even a `modified` on the round.
	 */
	_loadPreview() {
		NET()
			.call("qhse.api.facility_inspections.preview_scope", {
				inspection: state.roundName,
			})
			.then((payload) => {
				state.preview = payload || null;
				if (!state.mounted) return;
				this._paintQueue();
				if (state.tab === "capture" && !state.entry) this._paintMainEmpty();
			})
			.catch(() => {
				// A preview that cannot be computed is not an error worth a banner:
				// the screen still works, it just falls back to the plain empty
				// state rather than claiming a count it does not have.
				state.preview = null;
			});
	},

	_openIndex(index) {
		this._paintQueue();
		if (index < 0 || index >= state.entries.length) {
			state.index = -1;
			state.entry = null;
			this._paintMainEmpty();
			this._gate();
			return Promise.resolve();
		}

		state.index = index;
		const row = state.entries[index];
		R().clear(state.nodes.main).appendChild(R().empty(__("Loading the asset…"), ""));

		return NET()
			.call("qhse.api.facility_inspections.entry_form", { entry: row.name })
			.then((payload) => {
				state.entry = (payload && payload.entry) || {};
				state.asset = (payload && payload.asset) || {};
				state.version = (payload && payload.version) || {};
				state.questions = (payload && payload.questions) || [];
				state.answers = {};
				state.dirty = {};
				((payload && payload.responses) || []).forEach((response) => {
					const code = response.question_code;
					if (!code) return;
					state.answers[code] = {
						answer_text: response.answer_text,
						answer_number: response.answer_number,
						answer_check: response.answer_check,
						answer_select: response.answer_select,
						answer_date: response.answer_date,
						attachment_count: response.attachment_count,
						not_applicable: response.not_applicable,
						text_acknowledged: response.text_acknowledged,
						stored_verdict: response.verdict,
						stored_reason: response.verdict_reason,
					};
				});
				this._evaluateAll();
				this._paintEntry();
				this._paintQueue();
				this._gate();
			})
			.catch((error) => {
				R().clear(state.nodes.main).appendChild(
					R().empty(
						__("This asset could not be opened."),
						error && error.message ? String(error.message) : ""
					)
				);
				this._gate();
			});
	},

	_answerOf(code) {
		if (!state.answers[code]) state.answers[code] = {};
		return state.answers[code];
	},

	/** Emptying the answer box withdraws the acknowledgement (M9.19).
	 *
	 * **Emptying it, not editing it.** The assertion is *"I have given a written
	 * answer"*, not *"this exact prose"* — making a typo fix cost a second tap on
	 * the phone screen buys no safety, while an acknowledgement standing over an
	 * empty box is the assertion turned into a lie.
	 *
	 * The engine does not depend on this happening: `text_acknowledged` requires
	 * non-empty text server-side too, so a stale `1` is ignored rather than
	 * honoured (`facility_compliance.text_acknowledged`). This is so the screen
	 * never shows a ✓ that means nothing — and it is a write to module state, not
	 * a healing hook on a save (CLAUDE.md §15.13).
	 */
	_forgetAcknowledgementIfBlank(answer) {
		if (!String(answer.answer_text || "").trim()) answer.text_acknowledged = 0;
	},

	/** Has the inspector given this question an answer?
	 *
	 * **Not** "has the engine graded it" — M9.16 changed §8.5's `2/3` section
	 * counter from the second question to the first, and they are different
	 * questions for two of the eight answer types. `Short Text` and `Long Text`
	 * are **always** `Pending` by design (`rules.js`: blank is *"not answered"*,
	 * filled is *"judged by the inspector"*), because no rule can decide free
	 * prose — that is `D-j`, not a gap. Counting graded answers therefore made a
	 * section holding one text question permanently incomplete: a finished entry
	 * read `2/3` and an inspector had no way to make it read `3/3`.
	 *
	 * The wireframe's counter is progress through the section, so it counts what
	 * the inspector has filled in. The denominator stays every question in the
	 * section — hiding the text question from the denominator instead would make
	 * `2/2` mean "two of the three, and one is not shown to you".
	 *
	 * Which field an answer type writes into is `rules.answerField()`, the
	 * engine's own mapping, so this adds no second opinion about the schema
	 * (CONVENTIONS #24).
	 *
	 * `not_applicable` counts as answered: ticking N/A is an answer, and it is
	 * the one the verdict ladder already treats as one.
	 *
	 * **M9.19 narrows one case back.** A *mandatory* text question counts as
	 * answered only once the inspector has explicitly marked it so — the same
	 * test the Save gate makes. A section that reads `3/3` beside a Save button
	 * that will not press is the confusion M9.16 fixed pointing the other way.
	 */
	_answered(question) {
		const answer = this._answerOf(question.question_code);
		if (RULES()._internals.flag(answer, "not_applicable")) return true;

		// M9.19 — a **mandatory** text question is answered when the inspector
		// has said so, not when the box has characters in it. Counting it as
		// answered on text alone would put the counter back in disagreement with
		// the Save gate, which is the confusion M9.16 fixed in the other
		// direction: a section reading 3/3 beside a disabled Save button.
		if (
			RULES()._internals.flag(question, "mandatory") &&
			RULES().TEXT_TYPES.indexOf(question.answer_type) !== -1
		) {
			return RULES()._internals.textAcknowledged({
				answer_type: question.answer_type,
				answer_text: answer.answer_text,
				text_acknowledged: answer.text_acknowledged,
			});
		}

		const field = RULES().answerField(question.answer_type);
		const value = answer[field];
		if (field === "attachment_count") {
			return RULES()._internals.integer(value, 0) > 0;
		}
		// `answer_check` is `null` until a pill is pressed and `0` or `1` after —
		// so `0` is an answer and a falsy test would silently lose every "No".
		return value !== null && value !== undefined && String(value).trim() !== "";
	},

	_evaluateAll() {
		state.live = {};
		state.questions.forEach((question) => {
			const code = question.question_code;
			state.live[code] = RULES().evaluate(question, this._answerOf(code), state.today);
		});
	},

	_paintEntry() {
		const main = R().clear(state.nodes.main);
		main.appendChild(this._assetHeader());
		state.nodes.questions = R().el("div", { class: "qhse-fac-questions" });
		main.appendChild(state.nodes.questions);
		this._paintQuestions();
	},

	/** §8.5's sticky header: tag · type · zone path · issue count. */
	_assetHeader() {
		const head = R().el("div", { class: "qhse-fac-asset" });
		const top = R().el("div", { class: "qhse-fac-asset-top" });
		R().append(
			top,
			R().el("strong", { class: "qhse-fac-asset-tag" }, state.asset.asset_tag || state.asset.name),
			this._issueChip()
		);
		R().append(
			head,
			top,
			R().el("div", { class: "qhse-fac-asset-name" }, state.asset.asset_name || ""),
			R().el(
				"div",
				{ class: "qhse-fac-asset-zone" },
				// `zone_path` is the stored breadcrumb (`B1 / Floor 2 / Room 201`).
				// §8.5 draws it with `›`, so the separator is swapped for display
				// only — the stored value is never rewritten.
				String(state.asset.zone_path || "").split(" / ").join(" › ")
			)
		);

		if (state.entry.status === state.outOfScope) {
			head.appendChild(
				R().banner(
					"warn",
					__("Out of scope"),
					__("A scope re-sync removed this asset from the round. Its answers are kept and it is read-only.")
				)
			);
		}
		return head;
	},

	_issueChip() {
		const fails = state.questions.filter(
			(question) => (state.live[question.question_code] || {}).verdict === "Non-Compliant"
		).length;
		if (!fails) return null;
		return R().el(
			"span",
			{ class: "qhse-fac-glyph is-bad qhse-fac-asset-issues" },
			__("{0} issue(s)", [String(fails)])
		);
	},

	/** Questions grouped under their `section_label` dividers, in `idx` order.
	 *
	 * The order is the version's own `idx` — the child table's ordering column and
	 * the only thing the builder's drag writes (ARCH §6.18). Sections appear in
	 * first-appearance order rather than alphabetically, because the template
	 * author's order is a walk around the asset.
	 */
	_paintQuestions() {
		const host = R().clear(state.nodes.questions);
		if (!state.questions.length) {
			host.appendChild(
				R().empty(
					__("This round's template has no questions."),
					__("A template version must be published with at least one question.")
				)
			);
			return;
		}

		const groups = [];
		const byLabel = {};
		state.questions
			.slice()
			.sort((a, b) => (a.idx || 0) - (b.idx || 0))
			.forEach((question) => {
				const label = (question.section_label || "").trim();
				const key = label || NO_SECTION;
				if (!byLabel[key]) {
					byLabel[key] = { label, questions: [] };
					groups.push(byLabel[key]);
				}
				byLabel[key].questions.push(question);
			});

		state.nodes.feedback = {};
		state.nodes.photo = {};
		state.nodes.ack = {};
		state.nodes.sectionCounts = [];

		groups.forEach((group) => {
			const section = R().el("section", { class: "qhse-fac-qsection" });
			const head = R().el("div", { class: "qhse-fac-qsection-head" });
			R().append(
				head,
				R().el(
					"h3",
					{ class: "qhse-fac-section-title" },
					group.label || __("Questions")
				)
			);
			const count = R().el("span", { class: "qhse-fac-qsection-count" }, "");
			head.appendChild(count);
			state.nodes.sectionCounts.push([count, group.questions]);
			section.appendChild(head);
			group.questions.forEach((question) => {
				section.appendChild(this._question(question));
			});
			host.appendChild(section);
		});

		this._paintFeedback();
	},

	_question(question) {
		const code = question.question_code;
		const block = R().el("div", {
			class: "qhse-fac-question",
			dataset: { qhseQuestion: code },
		});

		const label = R().el("div", { class: "qhse-fac-question-label" });
		label.appendChild(R().el("span", {}, question.question_text || code));
		if (RULES()._internals.flag(question, "mandatory")) {
			label.appendChild(
				R().el("span", { class: "qhse-fac-required", title: __("Mandatory") }, "*")
			);
		}
		block.appendChild(label);

		if (question.help_text) {
			block.appendChild(R().el("p", { class: "qhse-fac-question-help" }, question.help_text));
		}

		block.appendChild(this._control(question));

		const ack = this._acknowledgeControl(question);
		if (ack) block.appendChild(ack);

		if (RULES()._internals.flag(question, "allow_na")) {
			block.appendChild(
				R().check(__("Not applicable"), this._answerOf(code).not_applicable, (value) => {
					this._answerOf(code).not_applicable = value;
					this._touch(code);
				})
			);
		}

		const feedback = R().el("p", { class: "qhse-fac-feedback" }, "");
		state.nodes.feedback[code] = feedback;
		block.appendChild(feedback);

		const photo = R().el("div", { class: "qhse-fac-photo-prompt" });
		photo.hidden = true;
		state.nodes.photo[code] = photo;
		block.appendChild(photo);

		return block;
	},

	/** M9.19 — the one extra tap a **mandatory text** question costs, or `null`.
	 *
	 * `PHASE-9.md` §8.5 is the phone screen the owner said is *"mainly used on the
	 * phone"*, so the acknowledgement is bound by three rules and each of them is
	 * visible in this function:
	 *
	 * 1. **One tap.** A single button. No dialog, no confirm, no second screen.
	 * 2. **Only where it is needed.** `null` for every question that is not
	 *    `mandatory` **and** Short/Long Text — a non-mandatory text question gets
	 *    no extra control at all. The test iterates the engine's own
	 *    `ANSWER_TYPES`, so a ninth type cannot quietly acquire one.
	 * 3. **Impossible to satisfy accidentally.** A `click` on a `<button>`, which
	 *    fires only on a deliberate press or a keyboard activation. Never
	 *    `focus`, never `blur`, never "you typed something so we assumed" — an
	 *    acknowledgement that can happen while an inspector scrolls past is not
	 *    an assertion, and an assertion is the whole of what the owner chose.
	 *
	 * The button is **disabled while the answer box is empty**, with the reason
	 * beside it in text rather than only in a `title`: a `title` is invisible on a
	 * touch screen, and this is the screen where that matters. That is §8.5's own
	 * *"disabled with the reason … never a validation error on submit"*, applied
	 * one control down.
	 *
	 * Nothing is ever read back out of it (CLAUDE.md §15.6). The value lives in
	 * `state.answers[code].text_acknowledged`; the button is painted *from* state
	 * by `_paintAcknowledgements`, and its handler writes *into* state.
	 */
	_acknowledgeControl(question) {
		if (!RULES()._internals.flag(question, "mandatory")) return null;
		if (RULES().TEXT_TYPES.indexOf(question.answer_type) === -1) return null;

		const code = question.question_code;
		const wrap = R().el("div", { class: "qhse-fac-ack" });
		const button = R().el("button", {
			type: "button",
			class: "qhse-fac-ack-button",
			"aria-pressed": "false",
		});
		button.addEventListener("click", () => {
			const answer = this._answerOf(code);
			// Read from state, never from the control, and refuse the tap the
			// engine would ignore anyway — `text_acknowledged` on a blank box is
			// not an answer (`facility_compliance.text_acknowledged`).
			if (!String(answer.answer_text || "").trim()) return;
			answer.text_acknowledged = RULES()._internals.flag(answer, "text_acknowledged") ? 0 : 1;
			this._touch(code);
		});
		const note = R().el("span", { class: "qhse-fac-ack-note" }, "");
		R().append(wrap, button, note);
		state.nodes.ack[code] = { wrap, button, note };
		return wrap;
	},

	/** Paint every acknowledgement control from state. Called on every `_touch`.
	 *
	 * Split out of `_paintFeedback` only so the three things it decides read as
	 * three things: the label, the pressed state, and whether the control is
	 * available at all. All three are a function of `state.answers`, never of the
	 * DOM.
	 */
	_paintAcknowledgements() {
		const editable = this._editable();
		Object.keys(state.nodes.ack || {}).forEach((code) => {
			const nodes = state.nodes.ack[code];
			if (!nodes) return;
			const answer = this._answerOf(code);
			const hasText = !!String(answer.answer_text || "").trim();
			const on = hasText && !!RULES()._internals.flag(answer, "text_acknowledged");

			nodes.button.textContent = on ? __("✓ Answered") : __("Mark answered");
			nodes.button.setAttribute("aria-pressed", on ? "true" : "false");
			nodes.button.className = `qhse-fac-ack-button${on ? " is-on" : ""}`;
			nodes.button.disabled = !hasText || !editable;
			nodes.button.setAttribute(
				"title",
				hasText
					? __("Confirm you have written the answer this question requires")
					: __("Write the answer first")
			);
			// The reason, in text, because a `title` is invisible on a phone.
			nodes.note.textContent = hasText
				? ""
				: __("write the answer first — this question needs one");
		});
	},

	/** The control for one answer type — eight types, from the schema.
	 *
	 * The type list is `facility_compliance.ANSWER_TYPES` as it reaches the client
	 * on the question row; a type this function does not know renders a **text**
	 * input and says so in its feedback line rather than rendering nothing, because
	 * a question with no control is indistinguishable from a template that has no
	 * such question (D11's rule).
	 */
	_control(question) {
		const code = question.question_code;
		const answer = this._answerOf(code);
		const type = question.answer_type || "Short Text";
		const readOnly = !this._editable();

		if (type === "Numeric") {
			const row = R().el("div", { class: "qhse-fac-control is-numeric" });
			const input = R().input(null, "number", answer.answer_number, (value) => {
				answer.answer_number = value === "" ? null : value;
				this._touch(code);
			}, { live: true });
			input.control.disabled = readOnly;
			input.control.setAttribute("inputmode", "decimal");
			input.control.setAttribute("step", "any");
			R().append(row, input);
			if (question.uom) {
				// Guidance beside the input, never stored on the answer —
				// `RESPONSE_FIELDS` has no `uom` and the reason strings quote the
				// question's unit from the frozen version.
				row.appendChild(R().el("span", { class: "qhse-fac-uom" }, question.uom));
			}
			return row;
		}

		if (type === "Checkbox") {
			const wanted = question.compliant_when || "Checked";
			const pills = R().pills(
				[
					[1, __("Yes")],
					[0, __("No")],
				],
				answer.answer_check === null || answer.answer_check === undefined
					? null
					: RULES()._internals.flag(answer, "answer_check"),
				(value) => {
					if (readOnly) return;
					answer.answer_check = value;
					this._touch(code);
				}
			);
			const row = R().el("div", { class: "qhse-fac-control" });
			R().append(row, pills);
			row.appendChild(
				R().el(
					"span",
					{ class: "qhse-fac-hint" },
					wanted === "Unchecked"
						? __("Compliant when No")
						: __("Compliant when Yes")
				)
			);
			return row;
		}

		if (type === "Single Select") {
			const options = this._selectOptions(question);
			const row = R().el("div", { class: "qhse-fac-control" });
			if ((question.select_render || "Radio") === "Radio" && options.length <= 4) {
				row.appendChild(
					R().pills(options, answer.answer_select || null, (value) => {
						if (readOnly) return;
						answer.answer_select = value;
						this._touch(code);
					})
				);
			} else {
				const control = R().select(
					null,
					options,
					answer.answer_select || "",
					(value) => {
						answer.answer_select = value || null;
						this._touch(code);
					},
					{ blankLabel: __("Choose…") }
				);
				control.control.disabled = readOnly;
				row.appendChild(control);
			}
			return row;
		}

		if (type === "Date") {
			const row = R().el("div", { class: "qhse-fac-control" });
			const input = R().input(null, "date", answer.answer_date, (value) => {
				answer.answer_date = value || null;
				this._touch(code);
			});
			input.control.disabled = readOnly;
			row.appendChild(input);
			return row;
		}

		if (type === "Photo" || type === "Document") {
			return this._attachmentControl(question, type, readOnly);
		}

		if (type === "Long Text") {
			const row = R().el("div", { class: "qhse-fac-control" });
			const area = R().el("textarea", { class: "qhse-fac-input", rows: "3" });
			area.value = answer.answer_text || "";
			area.disabled = readOnly;
			area.addEventListener("input", () => {
				answer.answer_text = area.value;
				this._forgetAcknowledgementIfBlank(answer);
				this._touch(code);
			});
			row.appendChild(area);
			return row;
		}

		const row = R().el("div", { class: "qhse-fac-control" });
		const input = R().input(null, "text", answer.answer_text, (value) => {
			answer.answer_text = value;
			this._forgetAcknowledgementIfBlank(answer);
			this._touch(code);
		}, { live: true });
		input.control.disabled = readOnly;
		row.appendChild(input);
		return row;
	},

	/** The options one Single Select offers — the engine's own permitted set.
	 *
	 * `select_options` then any `non_compliant_options` line that is not already in
	 * it, which is exactly how `facility_compliance._evaluate_select` builds the
	 * list it quotes back to the inspector. Offering only `select_options` would
	 * hide a non-compliant answer on a template the publish gate happened to let
	 * through, and the inspector would have no way to record what they saw.
	 */
	_selectOptions(question) {
		const options = RULES()._internals.lines(question.select_options);
		RULES()._internals.lines(question.non_compliant_options).forEach((option) => {
			if (options.indexOf(option) === -1) options.push(option);
		});
		return options;
	},

	_attachmentControl(question, type, readOnly) {
		const code = question.question_code;
		const answer = this._answerOf(code);
		const row = R().el("div", { class: "qhse-fac-control is-attachment" });
		const button = R().el(
			"button",
			{ type: "button", class: "qhse-fac-attach" },
			type === "Photo" ? __("▸ Add photo") : __("▸ Add document")
		);
		button.disabled = readOnly;
		button.addEventListener("click", () => this._attach(question, type));
		const count = R().el(
			"span",
			{ class: "qhse-fac-hint" },
			__("{0} attached", [String(RULES()._internals.integer(answer.attachment_count, 0))])
		);
		state.nodes.photo[`${code}:count`] = count;
		return R().append(row, button, count);
	},

	// ----------------------------------------------------------- attachments

	/** Attach a file to the entry, then record that one more exists.
	 *
	 * Two arms for the same reason `net.call` has two: `frappe.ui.FileUploader`
	 * lives in `desk.bundle.js` and is simply absent on the portal, where the
	 * honest control is a native `<input type="file" capture="environment">` —
	 * which is also the control that opens a phone camera directly.
	 *
	 * Both arms post to frappe's **own** `upload_file` handler, which requires a
	 * logged-in user with `write` on the target document; there is no new upload
	 * endpoint and no `allow_guest` anywhere on this path.
	 *
	 * `attachment_count` is then written through `save_responses`, the same
	 * allowlisted endpoint every other answer uses (`RESPONSE_FIELDS` carries it).
	 * The count is the client's arithmetic, which is the shipped contract from
	 * M9.4 — recorded as debt rather than quietly relied on.
	 */
	_attach(question, type) {
		const code = question.question_code;
		const accept = type === "Photo" ? "image/*" : undefined;

		const done = () => {
			const answer = this._answerOf(code);
			answer.attachment_count =
				RULES()._internals.integer(answer.attachment_count, 0) + 1;
			this._touch(code);
			const label = state.nodes.photo[`${code}:count`];
			if (label) {
				label.textContent = __("{0} attached", [String(answer.attachment_count)]);
			}
		};

		if (window.frappe && frappe.ui && frappe.ui.FileUploader) {
			new frappe.ui.FileUploader({
				doctype: "QHSE Asset Inspection Entry",
				docname: state.entry.name,
				frm: null,
				restrictions: accept ? { allowed_file_types: [accept] } : {},
				allow_multiple: false,
				on_success: () => done(),
			});
			return;
		}

		const picker = R().el("input", { type: "file", accept: accept || null });
		picker.style.display = "none";
		picker.addEventListener("change", () => {
			const file = picker.files && picker.files[0];
			if (!file) return;
			this._uploadFile(file)
				.then(() => done())
				.catch((error) => {
					frappe.show_alert({
						message:
							error && error.message
								? String(error.message)
								: __("The file could not be uploaded."),
						indicator: "red",
					});
				})
				.then(() => picker.remove());
		});
		document.body.appendChild(picker);
		picker.click();
	},

	/** `POST /api/method/upload_file` as multipart, with the CSRF token.
	 *
	 * frappe's own handler, not one of ours. `is_private` is 1 so an inspection
	 * photograph is not readable by URL to anyone who guesses it.
	 */
	_uploadFile(file) {
		const form = new FormData();
		form.append("file", file, file.name);
		form.append("doctype", "QHSE Asset Inspection Entry");
		form.append("docname", state.entry.name);
		form.append("is_private", "1");

		const headers = {};
		const token = window.frappe && frappe.csrf_token;
		if (token) headers["X-Frappe-CSRF-Token"] = token;

		return fetch("/api/method/upload_file", {
			method: "POST",
			headers,
			credentials: "same-origin",
			body: form,
		}).then((response) => {
			if (!response.ok) {
				throw new Error(__("The file could not be uploaded ({0}).", [String(response.status)]));
			}
			return response.json();
		});
	},

	// -------------------------------------------------------------- feedback

	_touch(code) {
		state.dirty[code] = true;
		const question = state.questions.find((row) => row.question_code === code);
		if (question) {
			state.live[code] = RULES().evaluate(question, this._answerOf(code), state.today);
		}
		this._paintFeedback();
		this._gate();
	},

	/** Paint every live verdict, every photo prompt and every section count.
	 *
	 * One pass rather than per-field surgery: a numeric answer can change a
	 * section's count and the asset header's issue chip at the same time, and three
	 * separate update paths is three chances for one of them to be forgotten.
	 */
	_paintFeedback() {
		state.questions.forEach((question) => {
			const code = question.question_code;
			const live = state.live[code] || { verdict: "Pending", reason: "" };
			const node = state.nodes.feedback[code];
			if (node) {
				node.textContent = live.reason ? `${this._glyphFor(live.verdict)} ${live.reason}` : "";
				node.className = `qhse-fac-feedback is-${this._toneFor(live.verdict)}`;
			}
			const prompt = state.nodes.photo[code];
			if (prompt) {
				const wanted = RULES().photoWanted(question, live.verdict);
				// The consequence, not the call: the prompt is *visible* or it is
				// not, and `hidden` is what the test asserts.
				prompt.hidden = !wanted;
				if (wanted && !prompt.firstChild) {
					R().append(
						prompt,
						R().el("span", { class: "qhse-fac-photo-note" }, __("photo needed")),
						(() => {
							const button = R().el(
								"button",
								{ type: "button", class: "qhse-fac-attach" },
								__("▸ Add photo")
							);
							button.addEventListener("click", () => this._attach(question, "Photo"));
							return button;
						})()
					);
				}
			}
		});

		this._paintAcknowledgements();

		(state.nodes.sectionCounts || []).forEach(([node, questions]) => {
			const answered = questions.filter((question) => this._answered(question)).length;
			node.textContent = `${answered}/${questions.length}`;
		});

		const head = state.nodes.main && state.nodes.main.querySelector(".qhse-fac-asset-top");
		if (head) {
			const existing = head.querySelector(".qhse-fac-asset-issues");
			if (existing) existing.remove();
			const chip = this._issueChip();
			if (chip) head.appendChild(chip);
		}
	},

	_glyphFor(verdict) {
		if (verdict === "Compliant") return "✓";
		if (verdict === "Non-Compliant") return "⚠";
		if (verdict === "Not Applicable") return "—";
		return "·";
	},

	_toneFor(verdict) {
		if (verdict === "Compliant") return "ok";
		if (verdict === "Non-Compliant") return "bad";
		return "idle";
	},

	// ------------------------------------------------------------ action bar

	_actionBar() {
		const bar = R().el("footer", { class: "qhse-fac-actionbar" });
		state.nodes.action = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button is-ghost" },
			__("+ Action")
		);
		state.nodes.action.addEventListener("click", () => this._composeAction());

		state.nodes.save = R().el(
			"button",
			{ type: "button", class: "qhse-fac-button is-primary" },
			__("Save & next")
		);
		state.nodes.save.addEventListener("click", () => this._saveAndNext());

		return R().append(bar, state.nodes.action, state.nodes.save);
	},

	_editable() {
		if (!state.canWrite) return false;
		if (!state.entry) return false;
		return state.entry.status !== state.outOfScope;
	},

	/** Why Save is unavailable — **in the button's `title`**, never a throw.
	 *
	 * `PHASE-9.md` §8.5: *"fixed action bar disabled with the reason in its
	 * `title`, never a validation error on submit"*. The server keeps the same
	 * gate (`set_entry_status` refuses `Completed` while a mandatory response is
	 * `Pending`, naming the codes), so this is the client half of one rule rather
	 * than a second rule — and the server is still the one that decides.
	 */
	_gate() {
		const reason = this._blockingReason();
		const save = state.nodes.save;
		const action = state.nodes.action;
		if (save) {
			save.disabled = !!reason || state.busy;
			save.setAttribute("title", reason || __("Save this asset and open the next"));
		}
		if (action) {
			const blocked = !this._editable();
			action.disabled = blocked || state.busy;
			action.setAttribute(
				"title",
				blocked
					? __("This entry is read-only.")
					: __("Raise a corrective action against this asset")
			);
		}
		return reason;
	},

	_blockingReason() {
		if (!state.entry) return __("No asset is open.");
		if (!state.canWrite) return __("You have read-only access to this round.");
		if (state.entry.status === state.outOfScope) {
			return __("A scope re-sync removed this asset from the round.");
		}
		// **Deliberately the engine's own predicate, not `!this._answered(…)`**,
		// even though M9.16 moved the section counter to the second test.
		//
		// This gate is a *mirror* of the server's, not an opinion:
		// `rules.mandatoryOutstanding` is the line-for-line mirror of
		// `facility_compliance.mandatory_outstanding`, which is the single
		// function `entry_verdict`'s first rung and `set_entry_status`'s
		// `Completed` gate both call. The parity harness compares the two over
		// every fixture case, so a divergence fails a test rather than turning a
		// disabled button — §8.5's contract, "the reason in its `title`" — into a
		// server refusal after the press, which is the one thing that contract
		// forbids.
		//
		// **M9.19 is what made that gate have an exit.** Until it landed, a
		// mandatory Short/Long Text question made an entry uncompletable on both
		// sides — text is always `Pending` by design (`D-j`) — so the round's
		// `entries_pending` never reached 0 and `Submit for Approval` never
		// became available. The acknowledgement is the exit, and it is the
		// inspector's explicit assertion rather than an inference from the box
		// having characters in it.
		const outstanding = state.questions.filter((question) => {
			const answer = this._answerOf(question.question_code);
			return RULES().mandatoryOutstanding({
				verdict: (state.live[question.question_code] || {}).verdict,
				mandatory: question.mandatory,
				answer_type: question.answer_type,
				answer_text: answer.answer_text,
				text_acknowledged: answer.text_acknowledged,
			});
		});
		if (outstanding.length) {
			return __("{0} mandatory question(s) still unanswered: {1}", [
				String(outstanding.length),
				outstanding
					.slice(0, 4)
					.map((question) => question.question_code)
					.join(", "),
			]);
		}
		return "";
	},

	// ----------------------------------------------------------------- saves

	/** Save the dirty answers, complete the entry, open the next one.
	 *
	 * Two calls, in this order and never merged: `save_responses` writes the
	 * answers and re-scores the entry with the **server's** engine, and
	 * `set_entry_status` moves it to `Completed` against the server's own
	 * mandatory-response gate. Merging them into one endpoint would mean a
	 * client-decided status, which is the line ARCH §6.20 draws.
	 */
	_saveAndNext() {
		if (this._gate()) return Promise.resolve();
		state.busy = true;
		this._gate();

		return NET()
			.call("qhse.api.facility_inspections.save_responses", {
				entry: state.entry.name,
				answers: this._dirtyAnswers(),
			})
			.then(() =>
				NET().call("qhse.api.facility_inspections.set_entry_status", {
					entry: state.entry.name,
					status: "Completed",
				})
			)
			.then((payload) => {
				this._absorb(payload);
				frappe.show_alert({ message: __("Asset saved."), indicator: "green" });
				state.busy = false;
				const next = this._nextIndex();
				if (next < 0) {
					this._paintQueue();
					this._gate();
					frappe.show_alert({
						message: __("Every asset in this round is done."),
						indicator: "green",
					});
					return null;
				}
				return this._openIndex(next);
			})
			.catch((error) => {
				state.busy = false;
				this._gate();
				frappe.show_alert({
					message:
						error && error.message
							? String(error.message)
							: __("The answers could not be saved."),
					indicator: "red",
				});
			});
	},

	/** Only what changed, and only the allowlisted keys.
	 *
	 * `RESPONSE_FIELDS` is the server's allowlist and it refuses anything else **by
	 * name**; the live-verdict bookkeeping this module keeps beside each answer
	 * (`stored_verdict`, `stored_reason`) is deliberately not sent, because those
	 * are in `SERVER_WRITTEN` and would be refused with "that field is written by
	 * the server".
	 */
	_dirtyAnswers() {
		const fields = [
			"answer_text",
			"answer_number",
			"answer_check",
			"answer_select",
			"answer_date",
			"attachment_count",
			"not_applicable",
			"text_acknowledged",
		];
		return Object.keys(state.dirty).map((code) => {
			const answer = state.answers[code] || {};
			const row = { question_code: code };
			fields.forEach((field) => {
				if (answer[field] !== undefined) row[field] = answer[field];
			});
			return row;
		});
	},

	/** Fold `_entry_payload` back into the queue row and the open entry.
	 *
	 * The server returns the stored counters and verdicts after every write
	 * precisely so the screen can repaint without reproducing the compliance
	 * arithmetic in JavaScript — one definition, not two (ARCH §6.20).
	 */
	_absorb(payload) {
		if (!payload) return;
		state.dirty = {};
		if (state.entry) state.entry.status = payload.status || state.entry.status;
		const row = state.entries[state.index];
		if (row) {
			row.status = payload.status || row.status;
			row.verdict = payload.verdict;
			row.auto_verdict = payload.auto_verdict;
			row.compliance_rate = payload.compliance_rate;
			row.total_responses = payload.total_responses;
			row.responses_pending = payload.responses_pending;
			row.responses_non_compliant = payload.responses_non_compliant;
		}
		this._paintQueue();
	},

	_nextIndex() {
		for (let i = state.index + 1; i < state.entries.length; i += 1) {
			const row = state.entries[i];
			if (row.status === "Pending" || row.status === "In Progress") return i;
		}
		return this._firstOpenIndex();
	},

	// -------------------------------------------------------- action composer

	/** `[+ Action]` — a hand-rendered composer inside a frappe dialog shell.
	 *
	 * **The body is ours and the shell is frappe's** (ARCH §8.6's pattern): the
	 * dialog gives the backdrop, focus trap and stacking, and every control inside
	 * it is built by `qhse.facility.render` so nothing is ever read back out of a
	 * frappe control (§15.6). `frappe.ui.Dialog` appends its body to `<body>`,
	 * **outside** `.qhse-fac` — M9.9b's handoff names this — so the modal's own
	 * rules are declared at the stylesheet's top level, not nested under the page
	 * class. The same class of mistake as §10.30's shadow root, through a different
	 * boundary.
	 *
	 * `category` and `priority` come from `round_queue`'s `vocabularies`, read from
	 * `frappe.get_meta` on the server (§10.13). `assigned_to` defaults to the
	 * session user: the field is `reqd` on `QHSE Actions` and there is no Link
	 * control on the portal, so the honest default is the person standing in front
	 * of the asset, editable by anyone who means someone else.
	 */
	_composeAction() {
		if (!this._editable()) return;
		const draft = {
			subject: "",
			category: (state.vocabularies.action_category || [])[0] || "",
			priority: (state.vocabularies.action_priority || [])[0] || "",
			assigned_to: state.user || "",
			due_date: "",
			description: "",
			question_code: "",
		};

		const modal = R().dialog(__("Raise a corrective action"), __("Create"), (dialog) => {
			dialog.hide();
			this._createAction(draft);
		});

		const gate = () => {
			if (!draft.subject.trim()) {
				modal.setPrimary(false, __("The corrective action needs a subject."));
			} else if (!draft.assigned_to.trim()) {
				modal.setPrimary(false, __("The corrective action needs an assignee."));
			} else {
				modal.setPrimary(true, "");
			}
		};

		R().append(
			modal.body,
			R().input(__("Subject"), "text", draft.subject, (value) => {
				draft.subject = value;
				gate();
			}, { live: true }),
			R().select(
				__("Against question"),
				state.questions.map((question) => [
					question.question_code,
					`${question.question_code} · ${question.question_text || ""}`,
				]),
				draft.question_code,
				(value) => {
					draft.question_code = value;
				},
				{ blankLabel: __("The asset as a whole") }
			),
			R().select(
				__("Category"),
				state.vocabularies.action_category || [],
				draft.category,
				(value) => {
					draft.category = value;
				},
				{ blank: false }
			),
			R().select(
				__("Priority"),
				state.vocabularies.action_priority || [],
				draft.priority,
				(value) => {
					draft.priority = value;
				},
				{ blank: false }
			),
			R().input(__("Assigned to"), "text", draft.assigned_to, (value) => {
				draft.assigned_to = value;
				gate();
			}, { live: true }),
			R().input(__("Due date"), "date", draft.due_date, (value) => {
				draft.due_date = value;
			}),
			R().input(__("Description"), "text", draft.description, (value) => {
				draft.description = value;
			})
		);

		modal.dialog.show();
		// After `show()`, never before: a control has no value until it is
		// rendered, and a gate evaluated in the paint pass would run against an
		// empty form and disable the button for a reason that is not true
		// (CLAUDE.md §15.7's second bug).
		gate();
	},

	_createAction(draft) {
		const values = {
			subject: draft.subject.trim(),
			category: draft.category,
			priority: draft.priority,
			assigned_to: draft.assigned_to.trim(),
			due_date: draft.due_date || "",
			description: draft.description || "",
		};
		return NET()
			.call("qhse.api.facility_inspections.create_action", {
				entry: state.entry.name,
				values,
				question_code: draft.question_code || "",
			})
			.then(() => {
				frappe.show_alert({ message: __("Corrective action raised."), indicator: "green" });
			})
			.catch((error) => {
				frappe.show_alert({
					message:
						error && error.message
							? String(error.message)
							: __("The action could not be created."),
					indicator: "red",
				});
			});
	},

	// ------------------------------------------------------------------ scan

	/** Open frappe's print view for a round, or for one asset's entry.
	 *
	 * Two formats, because a round report and a single-asset record are two
	 * different documents bound to two different doctypes — which is how frappe
	 * print formats work, and trying to make one format do both would mean a
	 * parameter a Print Format cannot take.
	 */
	_printView(doctype, name, format) {
		const url =
			`/printview?doctype=${encodeURIComponent(doctype)}` +
			`&name=${encodeURIComponent(name)}` +
			`&format=${encodeURIComponent(format)}&no_letterhead=0&_lang=en`;
		window.open(url, "_blank");
	},

	_print() {
		if (!state.roundName) return;
		this._printView("QHSE Asset Inspection", state.roundName, "Facility Inspection Record");
	},

	/** M9.30 — print ONE asset's record, from its row in the queue.
	 *
	 * The owner's reason for wanting both: filing the round and handing a sheet
	 * to a technician are different jobs, and a 200-asset PDF is the wrong answer
	 * to the second.
	 */
	_printEntry(entry) {
		if (!entry) return;
		this._printView(
			"QHSE Asset Inspection Entry",
			entry,
			"Facility Asset Inspection Record"
		);
	},

	// ------------------------------------------------------- M9.32 lifecycle

	/** Paint the round's state, its timeliness, and the buttons it offers.
	 *
	 * **The server decides what is pressable.** `round_workflow` returns
	 * `get_transitions`' answer, which has already been filtered by the caller's
	 * roles and by each transition's condition — so the two `Approve` rows (one
	 * to `Completed` when no corrective action is outstanding, one to
	 * `Verification` when some are) arrive as a single button and the reviewer
	 * is never shown a choice that is really an arithmetic fact.
	 *
	 * A failure paints nothing rather than an error banner: the questionnaire
	 * below is the screen's job, and an inspector standing in front of an
	 * extinguisher should not lose it because a workflow lookup timed out.
	 */
	_refreshLifecycle() {
		const host = state.nodes.lifecycle;
		if (!host || !state.roundName) return;
		NET()
			.call("qhse.api.facility_inspections.round_workflow", {
				inspection: state.roundName,
			})
			.then((payload) => this._paintLifecycle(payload))
			.catch(() => R().clear(host));
	},

	_paintLifecycle(payload) {
		const host = state.nodes.lifecycle;
		if (!host || !payload) return;
		state.lifecycle = payload;
		R().clear(host);

		const chip = R().el(
			"span",
			{ class: `qhse-fac-state is-${_slug(payload.status)}` },
			payload.status || ""
		);
		host.appendChild(chip);

		// Timeliness is the OTHER question, and it is deliberately a separate
		// chip: the owner's rule is that a clock never writes the status, so a
		// screen that folded "Overdue" into the state would be saying the
		// opposite of what the model does.
		if (payload.timeliness && payload.timeliness !== "Unknown") {
			const late = payload.days_late
				? __("{0} ({1}d)", [payload.timeliness, String(payload.days_late)])
				: payload.timeliness;
			host.appendChild(
				R().el(
					"span",
					{ class: `qhse-fac-timeliness is-${_slug(payload.timeliness)}` },
					late
				)
			);
		}

		if (payload.status === "Cancelled" && payload.cancellation_reason) {
			host.appendChild(
				R().el(
					"span",
					{ class: "qhse-fac-lifecycle-note" },
					payload.cancellation_reason
				)
			);
		}

		const needsReason = payload.needs_reason || [];
		(payload.transitions || []).forEach((transition) => {
			const button = R().el(
				"button",
				{
					type: "button",
					class: `qhse-fac-button qhse-fac-lifecycle-action${
						transition.next_state === "Cancelled" ? " is-ghost" : " is-primary"
					}`,
					"data-action": transition.action,
				},
				transition.action
			);
			button.addEventListener("click", () =>
				this._pressAction(transition, needsReason.indexOf(transition.action) >= 0)
			);
			host.appendChild(button);
		});

		if (payload.can_force_complete && payload.status !== "Completed") {
			const force = R().el(
				"button",
				{
					type: "button",
					class: "qhse-fac-button is-ghost qhse-fac-lifecycle-force",
					title: __("Close this round without waiting for its corrective actions"),
				},
				__("Force complete")
			);
			force.addEventListener("click", () => this._forceComplete());
			host.appendChild(force);
		}
	},

	/** Press one transition, asking for a reason where the server needs one. */
	_pressAction(transition, needsReason) {
		const run = (reason) =>
			NET()
				.call("qhse.api.facility_inspections.apply_round_action", {
					inspection: state.roundName,
					action: transition.action,
					reason: reason || "",
				})
				.then(() => {
					frappe.show_alert({
						message: __("{0} is now {1}.", [
							state.roundName,
							transition.next_state,
						]),
						indicator: "green",
					});
					// The whole screen, not just the bar: starting a round
					// resolves its scope, so the questionnaire and the queue
					// both changed underneath us.
					this.render(state.container, state.roundName, state.options);
				})
				.catch((error) => {
					frappe.msgprint({
						title: __("Not applied"),
						message: frappe.utils.escape_html(
							(error && error.message) ||
								__("That action could not be applied.")
						),
						indicator: "red",
					});
				});

		if (!needsReason) return run("");
		this._askReason(
			__("Cancel {0}", [state.roundName]),
			__("Say why this round is not going ahead. The reason stays on the record."),
			(reason) => run(reason)
		);
	},

	_forceComplete() {
		this._askReason(
			__("Force complete {0}", [state.roundName]),
			__("This closes the round without waiting for its corrective actions. Say why."),
			(reason) =>
				NET()
					.call("qhse.api.facility_inspections.force_complete", {
						inspection: state.roundName,
						reason,
					})
					.then(() => this.render(state.container, state.roundName, state.options))
					.catch((error) =>
						frappe.msgprint({
							title: __("Not applied"),
							message: frappe.utils.escape_html(
								(error && error.message) || __("That could not be applied.")
							),
							indicator: "red",
						})
					)
		);
	},

	/** A one-field modal that will not submit empty.
	 *
	 * The gate is on the button with the reason in its title, never a validation
	 * error after the press — PHASE-9 §8.5's rule, and the same shape every
	 * other dialog in this module uses. The value is read from module state, not
	 * back out of the control (§15.6).
	 */
	_askReason(title, prompt, onDone) {
		let text = "";
		const modal = R().dialog(title, __("Confirm"), (dialog) => {
			if (!text.trim()) return;
			dialog.hide();
			onDone(text.trim());
		});
		modal.body.appendChild(R().el("p", { class: "qhse-fac-hint" }, prompt));
		const field = R().input(__("Reason"), "text", "", (value) => {
			text = value;
			modal.setPrimary(!!text.trim(), __("A reason is required."));
		});
		modal.body.appendChild(field);
		modal.setPrimary(false, __("A reason is required."));
		modal.dialog.show();
		field.control.focus();
	},

	_scan() {
		if (!qhse.facility.scan) {
			frappe.show_alert({
				message: __("The scanner is not available on this page."),
				indicator: "orange",
			});
			return;
		}
		qhse.facility.scan.open({
			round: state.roundName,
			onResolved: (resolved) => {
				// `jumpToAsset` is what says "not in this round" by name; the
				// scanner never decides. Then `revalidate()`, because nothing about
				// a programmatic jump fires a DOM event (§15.7).
				const moved = this.jumpToAsset(resolved.asset, resolved.tag);
				if (moved) this.revalidate();
				return moved;
			},
		});
	},
};
